<template>
  <PModal title="Historique des changements" size="xl" v-model="show">
    <template #activator="{ open }">
      <PTooltip label="Cliquer pour accéder à l'historique du parapheur" :delay="400" :offset="5" placement="right">
        <PButton @click="open" size="icon" mode="tertiary-borderless">
          <span class="sr-only"> Cliquer pour accéder à l'historique du parapheur </span>
          <OhVueIcon :name="historyIcon" scale="1.5" />
        </PButton>
      </PTooltip>
    </template>

    <template #default="{ titleId }">
      <div :aria-labelledby="titleId">
        <PPagination
          :current-page="page"
          :total="letterFileHistoryStore.list.total"
          :page-size="pageSize"
          @change="onPaginationChange"
          size-selector-position="hidden"
        />
        <PTable :table="table" caption="" :version="2" class="text-sm pb-2">
          <template #column.creationDate="{ row }">
            <span class="xl:whitespace-nowrap">
              {{ dayjs(row.creationDate).format("DD/MM/YYYY - HH[h]mm") }}
            </span>
          </template>
          <template #column.target="{ row }">
            <PTag size="sm" color="custom" :class="getTargetColorClass(row)" class="lg:whitespace-nowrap">{{
              getTargetLabel(row)
            }}</PTag>
          </template>
          <template #column.type="{ row }">
            <PTag size="sm" :class="getTypeColorClass(row)" :color="getTypeColor(row)">
              {{ getTypeLabel(row) }}
            </PTag>
          </template>
        </PTable>
        <PPagination
          :current-page="page"
          :total="letterFileHistoryStore.list.total"
          :page-size="pageSize"
          @change="onPaginationChange"
          size-selector-position="hidden"
        />
      </div>
    </template>

    <template #actions>
      <div class="flex items-center justify-end">
        <PButton ref="closeBtn" mode="primary" size="sm" @click="show = false"> Fermer </PButton>
      </div>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import {
  PModal,
  PButton,
  useIcons,
  PTag,
  PTable,
  PPagination,
  usePastaTable,
  type PastaTableColumnDefArgs,
  PTooltip,
} from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { OiHistory } from "oh-vue-icons/icons";
import { HistoryOperationType, HistoryTarget, type LetterFileHistoryDto } from "../dto/history.dto";
import { computed, ref, watch } from "vue";
import dayjs from "dayjs";
import { useLetterFileHistoryStore } from "../stores/letter-file-history.store";

interface Props {
  id: number;
}
const props = defineProps<Props>();

const letterFileHistoryStore = useLetterFileHistoryStore();
const [historyIcon] = useIcons(OiHistory);

async function refresh() {
  await letterFileHistoryStore.fetchList(props.id, {
    page: page.value,
    pageSize: pageSize.value,
  });
}
const show = ref<boolean>(false);
watch(
  () => show.value,
  async () => {
    if (show.value) {
      await refresh();
    }
  }
);

const columns: PastaTableColumnDefArgs<LetterFileHistoryDto>[] = [
  { type: "simple", key: "creationDate", label: "Date", weight: 5 },
  { type: "simple", key: "author", label: "Auteur", weight: 4 },
  { type: "simple", key: "target", label: "Cible", weight: 3, align: "center" },
  {
    type: "simple",
    key: "type",
    label: "Action",
    weight: 3,
    cssClass: "whitespace-nowrap",
    align: "center",
  },
  { type: "simple", key: "label", label: "Détail", weight: 17 },
];

const data = computed(() => {
  return letterFileHistoryStore.list.data;
});
const table = usePastaTable({ columns, data });

const page = ref<number>(1);
const pageSize = ref<number>(10);

async function onPaginationChange(pagination: { page: number; pageSize: number }) {
  if (pagination.pageSize !== pageSize.value) {
    page.value = 1;
  } else {
    page.value = pagination.page;
  }
  pageSize.value = pagination.pageSize;
  await refresh();
}

function getTargetLabel(el: LetterFileHistoryDto) {
  switch (el.target) {
    case HistoryTarget.DOC:
      return "document";
    case HistoryTarget.FOLDER:
      return "dossier";
    case HistoryTarget.FLUX:
      return "flux";
    case HistoryTarget["BASE-INFO"]:
    case HistoryTarget.DEFAULT:
      return "parapheur";
    case HistoryTarget["EXTERNAL-INFO"]:
      return "ref externe";
    case HistoryTarget.CCUSER:
      return "copie";
    case HistoryTarget.COMMENT:
      return "commentaire";
    case HistoryTarget.SIGNATURE_POSITION:
      return "position de signature";
    default:
      return "inconnu";
  }
}

function getTargetColorClass(el: LetterFileHistoryDto) {
  switch (el.target) {
    case HistoryTarget.DOC:
      return "bg-blue-cumulus-moon-732-active";
    case HistoryTarget.FOLDER:
      return "bg-blue-france-850";
    case HistoryTarget.FLUX:
      return "bg-pink-tuile-850";
    case HistoryTarget["BASE-INFO"]:
    case HistoryTarget.DEFAULT:
      return "bg-yellow-tournesol-moon-922";
    case HistoryTarget["EXTERNAL-INFO"]:
      return "bg-green-bourgeon-925";
    case HistoryTarget.CCUSER:
      return "bg-beige-gris-galet-925";
    case HistoryTarget.COMMENT:
      return "bg-brown-caramel-moon-901";
    case HistoryTarget.SIGNATURE_POSITION:
      return "bg-green-archipel-925";
    default:
      return "";
  }
}

function getTypeLabel(el: LetterFileHistoryDto) {
  switch (el.type) {
    case HistoryOperationType.CREATE:
      return "ajout";
    case HistoryOperationType.UPDATE:
      return "mise à jour";
    case HistoryOperationType.DELETE:
      return "suppression";
    case HistoryOperationType.DOWNLOAD:
      return "téléchargement";
    case HistoryOperationType.MAIL:
      return "envoi par mail";
    default:
      return "inconnu";
  }
}

function getTypeColor(el: LetterFileHistoryDto) {
  switch (el.type) {
    case HistoryOperationType.CREATE:
      return "primary";
    case HistoryOperationType.UPDATE:
      return "success";
    case HistoryOperationType.DELETE:
      return "error";
    case HistoryOperationType.DOWNLOAD:
      return "warning";
    case HistoryOperationType.MAIL:
      return "custom";
    default:
      return "default";
  }
}

function getTypeColorClass(el: LetterFileHistoryDto) {
  switch (el.type) {
    case HistoryOperationType.MAIL:
      return "bg-blue-ecume-925-hover";
    default:
      return "";
  }
}
</script>
